import AutoHeight from "embla-carousel-auto-height";
import { Info } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNextButton,
  CarouselPreviousButton,
} from "@/components/ui/carousel";
import {
  PhysicalCardShippingStatus,
  showsShippingStatus,
} from "@/features/card/physical-card";
import { getCardStatus } from "@/features/card/types/card";
import { isCardStatusActivationRequired } from "@/features/card/utils/card-status";
import { usePaymentMethods } from "@/features/payments";
import {
  canActivate,
  type PaymentAccountCard,
} from "@/features/payments/types/payment-account-cards";
import { cn } from "@/lib/utils";

import { CardViewer } from "../../components/card-viewer";

import { CardActions } from "./components/card-actions";
import { CardDisclaimer } from "./components/card-disclaimer";
import { getCardStatusTranslationKey } from "./helpers";

export interface VisualisationHandler {
  toggleCardNumberMask: () => void;
}

export const CardDetailsPage = () => {
  const { t } = useTranslation();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const navigate = useNavigate();
  const { cards, paymentMethodsLoading } = usePaymentMethods();
  const handlersRef = useRef<Record<string, VisualisationHandler>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibilityState, setVisibilityState] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const handleSelect = (api: NonNullable<CarouselApi>) => {
      const index = api.selectedScrollSnap();
      setActiveIndex(index);
    };
    carouselApi.on("select", handleSelect);
    return () => {
      carouselApi.off("select", handleSelect);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (!paymentMethodsLoading && cards.length === 0) {
      navigate("/payments");
    }
  }, [cards, paymentMethodsLoading, navigate]);

  // Handlers and visibility are keyed by card id: two cards can share a type
  // (e.g. a Highnote and a Stripe virtual card), but never an id.
  const registerHandler = useCallback(
    (cardId: string, handler: VisualisationHandler) => {
      handlersRef.current[cardId] = handler;
      setVisibilityState((prev) => ({
        ...prev,
        [cardId]: prev[cardId] ?? false,
      }));
    },
    []
  );

  const activeCard = cards[activeIndex];
  const activeCardId = activeCard?.id;
  const activeHandler = activeCardId ? handlersRef.current[activeCardId] : null;

  const toggleCardNumberMask = useCallback(() => {
    if (activeHandler && activeCardId) {
      activeHandler.toggleCardNumberMask();
      setVisibilityState((prev) => ({
        ...prev,
        [activeCardId]: !prev[activeCardId],
      }));
    }
  }, [activeHandler, activeCardId]);

  if (!activeCard) {
    return null;
  }

  const isNumberVisible = Boolean(
    activeCardId && visibilityState[activeCardId]
  );
  // Unknown provider statuses resolve to null and simply hide the badge.
  const activeCardStatus = getCardStatus(activeCard.status);

  const renderCard = (card: PaymentAccountCard) => {
    if (
      canActivate(card.card_type) &&
      isCardStatusActivationRequired(card.status) &&
      showsShippingStatus(card)
    ) {
      return <PhysicalCardShippingStatus card={card} />;
    }

    return (
      <CardViewer
        card={card}
        isVisible={visibilityState[card.id] ?? false}
        onRegisterHandler={registerHandler}
      />
    );
  };

  const renderCards = () => {
    if (cards.length > 1) {
      return (
        <Carousel
          setApi={setCarouselApi}
          className="w-full"
          plugins={[AutoHeight()]}
        >
          <CarouselContent className="ml-0 md:-ml-4 items-start">
            {cards.map((card) => (
              <CarouselItem
                className="basis-full md:basis-[80%] pl-0 md:pl-4"
                key={card.id}
              >
                {renderCard(card)}
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="flex gap-2 justify-end mt-2 md:flex w-full">
            <div className="flex-1 flex justify-center md:hidden">
              <div className="flex gap-2">
                {cards.map((card, index) => (
                  <button
                    key={card.id}
                    className={cn(
                      "size-2 rounded-full transition-all",
                      index === activeIndex ? "bg-primary w-4" : "bg-gray-300"
                    )}
                    onClick={() => {
                      if (carouselApi) {
                        carouselApi.scrollTo(index);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="hidden md:flex gap-2">
              <CarouselPreviousButton />
              <CarouselNextButton />
            </div>
          </div>
        </Carousel>
      );
    }

    return renderCard(cards[0]);
  };

  return (
    <div className="overflow-x-hidden">
      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-2">
        <h1 className="text-2xl font-bold">{activeCard.title}</h1>
        {activeCardStatus && (
          <div>
            <Badge variant="outline">
              <Info className="size-4 mr-2" />
              {t(getCardStatusTranslationKey(activeCardStatus))}
            </Badge>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="w-full">{renderCards()}</div>

        <div className="flex flex-row align-middle w-full">
          <div className="w-full">
            <CardActions
              visibilityHandler={{
                toggleCardNumberMask,
                isNumberVisible,
              }}
              card={activeCard}
            />
          </div>
        </div>

        <CardDisclaimer card={activeCard} />
      </div>
    </div>
  );
};
